import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BloquePlantilla } from "@/components/agente/BurbujaPlantilla";
import { ZonaBloqueado } from "@/components/agente/BloqueoCliente";
import { BotonRespuestasRapidas, useRespuestasRapidas } from "@/components/agente/RespuestasRapidas"; // PROMPT 149
import { BotonNota, NotaDelEquipo, NotaEquipo } from "@/components/agente/NotaEquipo"; // PROMPT 149
import {
  BarraRespuesta,
  BotonEmojis,
  CitaEnBurbuja,
  ContextoChat,
  EtiquetasMensaje,
  MAX_TEXTO_WA,
  MenuMensaje,
  NoSoportado,
  useAccionesChat,
  useContextoChat,
  useLongPress,
} from "@/components/agente/AccionesMensaje";
import {
  AlertTriangle,
  ArchiveRestore,
  ArrowDown,
  Bot,
  Zap,
  Check,
  CheckCheck,
  Clock,
  Download,
  FileText,
  GraduationCap,
  Headphones,
  ImageOff,
  Loader2,
  MapPin,
  Mic,
  Paperclip,
  Pause,
  Play,
  RotateCcw,
  SendHorizontal,
  Smartphone,
  Trash2,
  User,
  Video,
  X,
} from "lucide-react";

// PROMPT 123 (2026-10-12): el chat de WhatsApp de la plataforma, lo mas parecido posible a
// WhatsApp. Con la coexistencia, parte del equipo atiende solo desde aca.
//   - Burbujas blancas (cliente) y verdes (nosotros) sobre el fondo de WhatsApp, separadores
//     de dia, hora y tildes en cada mensaje (un tilde enviado, dos entregado, dos azules leido).
//   - Fotos (se abren en grande), audios y notas de voz (se escuchan ahi mismo, con
//     velocidad), videos, documentos, stickers, ubicaciones, contactos y reacciones.
//   - Abajo: escribir (Enter manda, Shift+Enter baja de renglon), adjuntar con el clip,
//     arrastrar o pegar (Ctrl+V) fotos y documentos, y grabar notas de voz con el microfono.
// Los archivos se piden al backend con la sesion, como todo en la plataforma.

// ------------------------------------------------------------------ fechas
// El backend guarda las fechas en UTC y las devuelve sin la "Z" del final: sin ella el
// navegador las tomaria como hora local y las corria varias horas.
export function aFecha(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  const s = String(ts);
  const d = new Date(/T\d{2}:\d{2}/.test(s) && !/([zZ]|[+-]\d{2}:?\d{2})$/.test(s) ? `${s}Z` : s);
  return Number.isNaN(d.getTime()) ? null : d;
}

const mismoDia = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

function ayerDe(d) {
  const x = new Date(d);
  x.setDate(x.getDate() - 1);
  return x;
}

export function horaCorta(ts) {
  const d = aFecha(ts);
  return d ? d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hour12: false }) : "";
}

// PROMPT 143: dd/mm/aaaa, para "Ver la conversación anterior".
function fechaCorta(d) {
  if (d === null) return "";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function etiquetaDia(d) {
  const hoy = new Date();
  if (mismoDia(d, hoy)) return "Hoy";
  if (mismoDia(d, ayerDe(hoy))) return "Ayer";
  if ((hoy - d) / 86400000 < 6) {
    const dia = d.toLocaleDateString("es-AR", { weekday: "long" });
    return dia.charAt(0).toUpperCase() + dia.slice(1);
  }
  return d.toLocaleDateString("es-AR", {
    day: "numeric",
    month: "long",
    year: d.getFullYear() === hoy.getFullYear() ? undefined : "numeric",
  });
}

// Como la lista de chats de WhatsApp: hoy la hora, "Ayer", el dia de la semana o la fecha.
export function horaLista(ts) {
  const d = aFecha(ts);
  if (!d) return "";
  const hoy = new Date();
  if (mismoDia(d, hoy)) return horaCorta(d);
  if (mismoDia(d, ayerDe(hoy))) return "Ayer";
  if ((hoy - d) / 86400000 < 6) return d.toLocaleDateString("es-AR", { weekday: "short" });
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function mmss(segundos) {
  const s = Math.max(0, Math.round(Number(segundos) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function tamano(bytes) {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

// ------------------------------------------------------------------ avatar
const COLORES_AVATAR = [
  "bg-emerald-500", "bg-sky-500", "bg-violet-500", "bg-amber-500",
  "bg-rose-500", "bg-teal-500", "bg-indigo-500", "bg-orange-500",
];

export function Avatar({ nombre, telefono, className = "h-10 w-10 text-sm", anillo }) {
  const partes = String(nombre || "").trim().split(/\s+/).filter(Boolean);
  const iniciales = partes
    .map((p) => Array.from(p).find((c) => /[A-Za-zÀ-ÿ0-9]/.test(c)))
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  let h = 0;
  for (const c of String(telefono || nombre || "")) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return (
    <div
      className={`shrink-0 rounded-full flex items-center justify-center text-white font-semibold ${COLORES_AVATAR[h % COLORES_AVATAR.length]} ${className}`}
      aria-hidden="true"
      // PROMPT 144: anillo en el color de la marca (lista y encabezado del chat).
      style={anillo ? { boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${anillo}` } : undefined}
      >
      {iniciales || <User className="h-1/2 w-1/2" />}
    </div>
  );
}

// ------------------------------------------------------------------ archivos
// Solo estos tipos se muestran "para ver". Cualquier otro archivo se baja (un HTML o un SVG
// que mande un cliente nunca se abre en el navegador).
const PARA_VER = [
  "image/jpeg", "image/png", "image/webp", "image/gif", "audio/ogg", "audio/mpeg", "audio/mp4",
  "audio/aac", "audio/amr", "video/mp4", "video/3gpp", "application/pdf",
];
const EXTENSIONES = {
  "image/jpeg": ".jpg", "image/png": ".png", "image/webp": ".webp", "image/gif": ".gif",
  "audio/ogg": ".ogg", "audio/mpeg": ".mp3", "audio/mp4": ".m4a", "audio/aac": ".aac",
  "audio/amr": ".amr", "video/mp4": ".mp4", "video/3gpp": ".3gp", "application/pdf": ".pdf",
};

function tipoSeguro(mime) {
  const m = String(mime || "").split(";")[0].trim().toLowerCase();
  return PARA_VER.includes(m) ? m : "application/octet-stream";
}

function nombreDe(m) {
  const media = m?.media || {};
  if (media.nombre) return media.nombre;
  const base = { imagen: "foto", audio: "audio", video: "video", sticker: "sticker" }[media.tipo] || "archivo";
  return `${base}${EXTENSIONES[media.mime] || ""}`;
}

// Se puede pedir al backend (si todavia no estaba bajado de Meta, lo baja en ese momento).
const puedeBajar = (media) => !["muy_grande", "no_disponible"].includes(media?.estado);

function textoNoDisponible(media) {
  if (media?.estado === "muy_grande") return media.error || "Es muy pesado para la plataforma: abrilo en el celular.";
  return "Este archivo ya no está disponible.";
}

async function mensajeDeError(e) {
  if (!e?.response) return "Sin conexión con el servidor. Probá de nuevo.";
  let detalle = e.response.data?.detail;
  const data = e.response.data;
  if (detalle === undefined && typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      detalle = JSON.parse(await data.text()).detail;
    } catch {
      detalle = undefined;
    }
  }
  return formatApiError(detalle);
}

const archivosEnMemoria = new Map(); // id del mensaje -> url del archivo (blob:)
const archivosEnCamino = new Map();
const MAX_EN_MEMORIA = 80;

function traerArchivo(id, mime) {
  if (archivosEnMemoria.has(id)) return Promise.resolve(archivosEnMemoria.get(id));
  if (archivosEnCamino.has(id)) return archivosEnCamino.get(id);
  const pedido = api
    .get(`/whatsapp/media/${id}`, { responseType: "blob" })
    .then(({ data }) => {
      const url = URL.createObjectURL(new Blob([data], { type: tipoSeguro(mime) }));
      archivosEnMemoria.set(id, url);
      if (archivosEnMemoria.size > MAX_EN_MEMORIA) {
        const [viejo, urlVieja] = archivosEnMemoria.entries().next().value;
        archivosEnMemoria.delete(viejo);
        URL.revokeObjectURL(urlVieja);
      }
      return url;
    })
    .finally(() => archivosEnCamino.delete(id));
  archivosEnCamino.set(id, pedido);
  return pedido;
}

function useArchivo(id, mime) {
  const [url, setUrl] = useState(() => archivosEnMemoria.get(id) || null);
  const [estado, setEstado] = useState(() => (archivosEnMemoria.has(id) ? "listo" : "nada"));
  const [error, setError] = useState(null);
  const cargar = useCallback(async () => {
    setEstado("cargando");
    setError(null);
    try {
      const u = await traerArchivo(id, mime);
      setUrl(u);
      setEstado("listo");
      return u;
    } catch (e) {
      setError(await mensajeDeError(e));
      setEstado("error");
      return null;
    }
  }, [id, mime]);
  return { url, estado, error, cargar };
}

// Las fotos se piden recien cuando aparecen en pantalla (una conversacion larga no baja todo).
function useVisible(ref, activo) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!activo || visible) return undefined;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const obs = new IntersectionObserver((entradas) => {
      if (entradas.some((x) => x.isIntersecting)) {
        setVisible(true);
        obs.disconnect();
      }
    }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, activo, visible]);
  return visible;
}

function bajarUrl(url, nombre) {
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre || "archivo";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// ------------------------------------------------------------------ partes de un mensaje
function NoDisponible({ media, icono: Icono }) {
  return (
    <div className="flex w-64 max-w-full items-center gap-2.5 rounded-md bg-black/[0.04] p-2.5 text-xs text-slate-500">
      <Icono className="h-6 w-6 shrink-0 text-slate-400" />
      <span>{textoNoDisponible(media)}</span>
    </div>
  );
}

function Imagen({ m, sticker, onCargado, onAmpliar }) {
  const media = m.media;
  const caja = useRef(null);
  const posible = puedeBajar(media);
  const visible = useVisible(caja, posible);
  const { url, estado, error, cargar } = useArchivo(m.id, media.mime);
  useEffect(() => {
    if (visible && estado === "nada") cargar();
  }, [visible, estado, cargar]);
  if (!posible) return <NoDisponible media={media} icono={ImageOff} />;
  if (url) {
    return (
      <button
        type="button"
        onClick={() => onAmpliar({ url, nombre: nombreDe(m), leyenda: media.caption })}
        className="block cursor-zoom-in"
        title="Ver en grande"
      >
        <img
          src={url}
          alt={media.caption || (sticker ? "Sticker" : "Foto")}
          onLoad={onCargado}
          className={sticker ? "block h-32 w-32 object-contain" : "block max-h-80 w-auto max-w-full rounded-md object-cover"}
          data-testid={`wa-imagen-${m.id}`}
        />
      </button>
    );
  }
  return (
    <div
      ref={caja}
      className={`flex flex-col items-center justify-center gap-2 rounded-md bg-slate-200/70 text-slate-500 ${sticker ? "h-32 w-32" : "h-44 w-64 max-w-full"}`}
      data-testid={`wa-imagen-cargando-${m.id}`}
    >
      {estado === "error" ? (
        <>
          <AlertTriangle className="h-5 w-5" />
          <span className="px-3 text-center text-xs">{error}</span>
          <button type="button" onClick={cargar} className="text-xs font-medium text-emerald-700 hover:underline">
            Reintentar
          </button>
        </>
      ) : (
        <Loader2 className="h-5 w-5 animate-spin" />
      )}
    </div>
  );
}

const VELOCIDADES = [1, 1.5, 2];

function Audio({ m, entrante }) {
  const media = m.media;
  const posible = puedeBajar(media);
  const { url, estado, error, cargar } = useArchivo(m.id, media.mime);
  const audio = useRef(null);
  const quiereSonar = useRef(false);
  const [sonando, setSonando] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(Number(media.duracion_s) || 0);
  const [vel, setVel] = useState(1);
  const [noSuena, setNoSuena] = useState(false);

  const reproducir = useCallback(() => {
    const a = audio.current;
    if (!a) return;
    a.play().catch((e) => {
      if (e?.name !== "NotAllowedError" && e?.name !== "AbortError") setNoSuena(true);
    });
  }, []);
  useEffect(() => {
    if (url && quiereSonar.current) {
      quiereSonar.current = false;
      reproducir();
    }
  }, [url, reproducir]);
  useEffect(() => {
    if (audio.current) audio.current.playbackRate = vel;
  }, [vel, url]);

  if (!posible) return <NoDisponible media={media} icono={Mic} />;
  const alternar = () => {
    if (!url) {
      quiereSonar.current = true;
      cargar();
      return;
    }
    if (audio.current?.paused) reproducir();
    else audio.current?.pause();
  };
  const total = dur || 0;
  return (
    <div className="flex w-64 max-w-full items-center gap-2.5 py-1" data-testid={`wa-audio-${m.id}`}>
      <button
        type="button"
        onClick={alternar}
        disabled={estado === "cargando"}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-600 hover:bg-black/5 hover:text-slate-900"
        title={sonando ? "Pausa" : "Escuchar"}
        data-testid={`wa-audio-play-${m.id}`}
      >
        {estado === "cargando" ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : sonando ? (
          <Pause className="h-5 w-5 fill-current" />
        ) : (
          <Play className="h-5 w-5 fill-current" />
        )}
      </button>
      <div className="min-w-0 flex-1">
        <input
          type="range"
          min={0}
          max={total || 1}
          step={0.1}
          value={Math.min(pos, total || 1)}
          disabled={!url}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (audio.current) audio.current.currentTime = v;
            setPos(v);
          }}
          className="h-1 w-full cursor-pointer accent-emerald-600"
          aria-label="Posición del audio"
        />
        <div className="mt-0.5 flex items-center justify-between text-[11px] text-slate-500">
          <span className="tabular-nums">{sonando || pos > 0 ? mmss(pos) : total ? mmss(total) : "—"}</span>
          {sonando || vel !== 1 ? (
            <button
              type="button"
              onClick={() => setVel(VELOCIDADES[(VELOCIDADES.indexOf(vel) + 1) % VELOCIDADES.length])}
              className="rounded-full bg-slate-500/80 px-1.5 text-[10px] font-semibold text-white"
              title="Velocidad"
              data-testid={`wa-audio-velocidad-${m.id}`}
            >
              {String(vel).replace(".", ",")}×
            </button>
          ) : null}
        </div>
        {estado === "error" && <div className="text-[11px] text-red-600">{error}</div>}
        {noSuena && url && (
          <button type="button" onClick={() => bajarUrl(url, nombreDe(m))} className="text-[11px] text-emerald-700 hover:underline">
            Este navegador no lo puede reproducir: descargar
          </button>
        )}
      </div>
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
          media.voz ? (entrante ? "bg-slate-200 text-slate-500" : "bg-emerald-200 text-emerald-700") : "bg-orange-400 text-white"
        }`}
        title={media.voz ? "Nota de voz" : "Audio"}
      >
        {media.voz ? <Mic className="h-5 w-5" /> : <Headphones className="h-5 w-5" />}
      </div>
      {url && (
        <audio
          ref={audio}
          src={url}
          preload="metadata"
          onPlay={() => setSonando(true)}
          onPause={() => setSonando(false)}
          onEnded={() => {
            setSonando(false);
            setPos(0);
          }}
          onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            if (Number.isFinite(d) && d > 0) setDur(d);
          }}
          onError={() => setNoSuena(true)}
        />
      )}
    </div>
  );
}

function VideoMsg({ m }) {
  const media = m.media;
  const posible = puedeBajar(media);
  const { url, estado, error, cargar } = useArchivo(m.id, media.mime);
  if (!posible) return <NoDisponible media={media} icono={Video} />;
  if (url) {
    return (
      <video src={url} controls autoPlay className="block max-h-80 max-w-full rounded-md bg-black" data-testid={`wa-video-${m.id}`} />
    );
  }
  return (
    <button
      type="button"
      onClick={cargar}
      className="flex h-40 w-64 max-w-full flex-col items-center justify-center gap-2 rounded-md bg-slate-800/90 text-white"
      data-testid={`wa-video-ver-${m.id}`}
    >
      {estado === "cargando" ? (
        <Loader2 className="h-7 w-7 animate-spin" />
      ) : (
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-black/40">
          <Play className="h-6 w-6 fill-current" />
        </span>
      )}
      <span className="px-3 text-center text-xs text-white/80">
        {estado === "error" ? error : `Video${media.bytes ? ` · ${tamano(media.bytes)}` : ""}`}
      </span>
    </button>
  );
}

const COLOR_DOCUMENTO = {
  pdf: "text-red-500", doc: "text-sky-600", docx: "text-sky-600", xls: "text-emerald-600",
  xlsx: "text-emerald-600", ppt: "text-orange-500", pptx: "text-orange-500",
};

function Documento({ m }) {
  const media = m.media;
  const posible = puedeBajar(media);
  const { estado, error, cargar } = useArchivo(m.id, media.mime);
  const nombre = nombreDe(m);
  const ext = nombre.includes(".") ? nombre.split(".").pop().toLowerCase() : "";
  const esPdf = media.mime === "application/pdf" || ext === "pdf";
  const usar = async (abrir) => {
    const url = await cargar();
    if (!url) return;
    if (abrir) window.open(url, "_blank");
    else bajarUrl(url, nombre);
  };
  return (
    <div className="w-64 max-w-full" data-testid={`wa-doc-${m.id}`}>
      <div className="flex items-center gap-2.5 rounded-md bg-black/[0.04] p-2.5">
        <FileText className={`h-8 w-8 shrink-0 ${COLOR_DOCUMENTO[ext] || "text-slate-500"}`} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-slate-800" title={nombre}>{nombre}</div>
          <div className="text-[11px] uppercase text-slate-500">
            {[ext, media.bytes ? tamano(media.bytes) : null].filter(Boolean).join(" · ")}
          </div>
        </div>
        {posible && (
          <button
            type="button"
            onClick={() => usar(false)}
            className="shrink-0 rounded-full p-1.5 text-slate-500 hover:bg-black/5 hover:text-slate-800"
            title="Descargar"
            data-testid={`wa-doc-bajar-${m.id}`}
          >
            {estado === "cargando" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
          </button>
        )}
      </div>
      {posible && esPdf && (
        <button
          type="button"
          onClick={() => usar(true)}
          className="mt-1 w-full rounded-md py-1 text-xs font-medium text-emerald-700 hover:bg-black/5"
          data-testid={`wa-doc-abrir-${m.id}`}
        >
          Abrir
        </button>
      )}
      {!posible && <div className="mt-1 text-[11px] text-slate-500">{textoNoDisponible(media)}</div>}
      {estado === "error" && <div className="mt-1 text-[11px] text-red-600">{error}</div>}
    </div>
  );
}

function Ubicacion({ u }) {
  const lat = Number(u?.lat);
  const lng = Number(u?.lng);
  const hayMapa = u?.lat != null && u?.lng != null && Number.isFinite(lat) && Number.isFinite(lng);
  const contenido = (
    <>
      <MapPin className="h-8 w-8 shrink-0 text-red-500" />
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-800">{u?.nombre || "Ubicación"}</div>
        {u?.direccion && <div className="text-xs text-slate-500">{u.direccion}</div>}
        {hayMapa && <div className="text-xs font-medium text-emerald-700">Ver en el mapa</div>}
      </div>
    </>
  );
  const cls = "flex w-64 max-w-full items-center gap-2.5 rounded-md bg-black/[0.04] p-2.5";
  return hayMapa ? (
    <a
      href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
      target="_blank"
      rel="noopener noreferrer"
      className={`${cls} hover:bg-black/[0.07]`}
    >
      {contenido}
    </a>
  ) : (
    <div className={cls}>{contenido}</div>
  );
}

function Contactos({ contactos }) {
  return (
    <div className="space-y-1.5">
      {(contactos || []).map((c, i) => (
        <div key={i} className="flex w-64 max-w-full items-center gap-2.5 rounded-md bg-black/[0.04] p-2.5">
          <Avatar nombre={c.nombre} telefono={(c.telefonos || [])[0]} className="h-9 w-9 text-xs" />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-slate-800">{c.nombre || "Contacto"}</div>
            {(c.telefonos || []).map((t) => (
              <div key={t} className="text-xs text-slate-500">{t}</div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Mensajes de antes de este cambio: solo quedo "[image]", "[audio]"... sin el archivo.
const LEGADO = {
  image: "📷 Foto", imagen: "📷 Foto", audio: "🎤 Audio", voice: "🎤 Audio", video: "🎥 Video",
  document: "📄 Documento", documento: "📄 Documento", sticker: "Sticker",
  location: "📍 Ubicación", ubicacion: "📍 Ubicación", contacts: "👤 Contacto", contacto: "👤 Contacto",
  reaction: "Reacción", reaccion: "Reacción", unsupported: "Mensaje que WhatsApp no deja ver acá",
};
const LEGADO_CON_ARCHIVO = new Set(["image", "imagen", "audio", "voice", "video", "document", "documento", "sticker"]);

function claveLegado(texto) {
  const r = /^\[([a-z]+)\]$/i.exec(String(texto || "").trim());
  return r && LEGADO[r[1].toLowerCase()] ? r[1].toLowerCase() : null;
}

function quien(m) {
  if (m.direccion === "entrante") return "Cliente";
  if (m.autor === "agente") return "Agente";
  if (m.autor === "automatico") return "Mensaje automático";
  if (m.autor === "difusion") return "Difusión"; // PROMPT 151
  if (m.desde_celular) return "Celular";
  return m.usuario || "Equipo";
}

function Tildes({ m }) {
  const e = m.estado_envio;
  // PROMPT 148: "Reproducido" (el cliente escuchó la nota de voz) también va con dos azules.
  const dos = (color, titulo) => (
    <span title={titulo} className="inline-flex">
      <CheckCheck className={`h-4 w-4 ${color}`} />
    </span>
  );
  if (e === "fallido" || e === "failed") return <AlertTriangle className="h-3.5 w-3.5 text-red-500" />;
  if (e === "played") return dos("text-sky-500", "Reproducido");
  if (e === "read") return dos("text-sky-500", "Leído");
  if (e === "delivered") return dos("text-slate-400", "Entregado");
  if (e === "enviado" || e === "sent") return <Check className="h-4 w-4 text-slate-400" />;
  return <Clock className="h-3 w-3 text-slate-400" />;
}

// PROMPT 129 (2026-10-15): lo que el agente uso de la guia de Nueva York, para que el equipo lo vea
// en el chat ("usó la guía (clima, ruta)"). Si el clima o los eventos no contestaron, lo dice.
const GUIA_FUENTES = { guia_hora: "hora", guia_clima: "clima", guia_ruta: "ruta", guia_eventos: "eventos" };

function resumenDeGuia(acciones) {
  if (!acciones.some((a) => a.herramienta === "guia_ny")) return null;
  const partes = Object.keys(GUIA_FUENTES)
    .filter((h) => acciones.some((a) => a.herramienta === h))
    .map((h) => (acciones.some((a) => a.herramienta === h && a.resultado?.ok === false) ? `${GUIA_FUENTES[h]} sin datos` : GUIA_FUENTES[h]));
  if (acciones.some((a) => a.herramienta === "guia_ny_error")) partes.push("sin datos en vivo");
  return `usó la guía${partes.length ? ` (${partes.join(", ")})` : ""}`;
}

// ------------------------------------------------------------------ burbuja
function Burbuja({ m, real, mostrarRemitente, primero, reacciones, onEnsenar, onCargado, onAmpliar }) {
  // PROMPT 148: menú de acciones del mensaje (flecha al pasar el mouse; apretar largo en el celular).
  const ctx = useContextoChat();
  const [menu, setMenu] = useState(false);
  const abrirMenu = useCallback(() => setMenu(true), []);
  const largo = useLongPress(abrirMenu);
  const eliminado = !!m.eliminado;
  const motivoFallo = String(m.error_explicado || m.error || "").slice(0, 160);
  const descargar = useCallback(async () => {
    const archivo = m.media || {};
    try {
      const url = await traerArchivo(m.id, archivo.mime);
      bajarUrl(url, archivo.nombre || "archivo");
    } catch {
      toast.error("No se pudo bajar el archivo.");
    }
  }, [m.id, m.media]);
  const entrante = m.direccion === "entrante";
  const media = m.media;
  const tipo = media?.tipo;
  const sticker = tipo === "sticker";
  const legado = !media && !m.ubicacion && !m.contactos ? claveLegado(m.texto) : null;
  // PROMPT 147: una plantilla mandada (con o sin archivo de encabezado) se ve como la recibe el cliente.
  const plantilla = m.tipo === "plantilla" ? m.plantilla || {} : null;
  const leyenda = media && !plantilla ? media.caption : null;
  const remitente = quien(m);
  const acciones = m.acciones || [];
  const notas = [
    acciones.some((a) => a.herramienta === "disponibilidad") ? "consultó cupos" : null,
    resumenDeGuia(acciones),
    acciones.some((a) => a.herramienta === "derivar") ? "derivó" : null,
  ].filter(Boolean);
  const fallido = !entrante && (m.estado_envio === "fallido" || m.estado_envio === "failed");

  let cuerpo;
  if (media) {
    if (tipo === "imagen" || sticker) cuerpo = <Imagen m={m} sticker={sticker} onCargado={onCargado} onAmpliar={onAmpliar} />;
    else if (tipo === "audio") cuerpo = <Audio m={m} entrante={entrante} />;
    else if (tipo === "video") cuerpo = <VideoMsg m={m} />;
    else cuerpo = <Documento m={m} />;
  } else if (m.tipo === "ubicacion" && m.ubicacion) {
    cuerpo = <Ubicacion u={m.ubicacion} />;
  } else if (m.tipo === "contacto" && m.contactos) {
    cuerpo = <Contactos contactos={m.contactos} />;
  } else if (m.tipo === "reaccion") {
    cuerpo = <span className="text-sm text-slate-600">Reaccionó {m.reaccion?.emoji || ""} a un mensaje</span>;
  } else if (legado) {
    cuerpo = (
      <div className="text-sm text-slate-500">
        <span className="italic">{LEGADO[legado]}</span>
        {LEGADO_CON_ARCHIVO.has(legado) && (
          <div className="text-[11px] text-slate-400">Llegó antes de que la plataforma guardara los archivos: está en el celular.</div>
        )}
      </div>
    );
  }
  // PROMPT 148: lo que WhatsApp avisa pero no deja ver (encuestas, GIF...).
  if (m.no_soportado && !media) {
    cuerpo = <NoSoportado tipo={m.no_soportado} atiendeCelular={!!(ctx && ctx.atiendeCelular)} />;
  }
  if (plantilla) {
    const bloque = <BloquePlantilla partes={plantilla.partes} texto={m.texto} nombre={plantilla.nombre} />;
    cuerpo = media ? (
      <>
        {cuerpo}
        {bloque}
      </>
    ) : (
      <>
        {bloque}
        <div className="h-4" />
      </>
    );
  }
  const conTexto = !cuerpo;
  const sinFondo = sticker && !leyenda;
  const colorRemitente = m.autor === "agente" ? "text-violet-700" : m.autor === "automatico" || m.autor === "difusion" ? "text-amber-700" : m.desde_celular ? "text-sky-700" : "text-emerald-700";

  return (
    <div
      className={`flex items-end gap-1.5 ${entrante ? "justify-start" : "justify-end"} ${primero ? "mt-2.5" : "mt-0.5"} ${reacciones.length ? "mb-3.5" : ""}`}
      data-testid={`wa-msg-${m.id}`}
    >
      <div className={`flex min-w-0 max-w-[85%] flex-col sm:max-w-[70%] ${entrante ? "items-start" : "items-end"}`}>
        <div
          {...largo}
          className={`group relative max-w-full rounded-lg ${
            sinFondo ? "" : `shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] ${entrante ? "bg-white" : "bg-[#d9fdd3]"}`
          } ${primero && !sinFondo ? (entrante ? "rounded-tl-none" : "rounded-tr-none") : ""} ${
            conTexto ? "px-2.5 pt-1.5 pb-2" : "p-1"
          }`}
        >
          <MenuMensaje
          m={m}
          abierto={menu}
          setAbierto={setMenu}
          reacciones={reacciones}
          texto={media ? leyenda || "" : m.texto || ""}
          tieneArchivo={!!media}
          onDescargar={descargar}
        />
        {mostrarRemitente && !entrante && (
            <div className={`mb-0.5 flex items-center gap-1 text-[12px] font-medium ${colorRemitente} ${conTexto ? "" : "px-1.5 pt-0.5"}`}>
              {m.autor === "agente" ? <Bot className="h-3.5 w-3.5" /> : m.autor === "automatico" || m.autor === "difusion" ? <Zap className="h-3.5 w-3.5" /> : m.desde_celular ? <Smartphone className="h-3.5 w-3.5" /> : null}
              <span>{remitente}</span>
              {notas.length ? <span className="font-normal text-slate-500">· {notas.join(" · ")}</span> : null}
            </div>
          )}
          <EtiquetasMensaje m={m} />
        <CitaEnBurbuja m={m} entrante={entrante} />
        {eliminado ? (
          <div
            className="px-1 pb-3 pt-0.5 text-[13px] italic text-slate-500"
            data-testid={`wa-eliminado-${m.id}`}
          >
            {entrante ? "🚫 El cliente eliminó este mensaje" : "🚫 Se eliminó este mensaje"}
          </div>
        ) : conTexto ? (
            <div className="whitespace-pre-wrap break-words text-[14px] leading-[19px] text-[#111b21]">
              {m.texto}
              <span className={`inline-block ${entrante ? "w-10" : "w-14"}`} aria-hidden="true" />
            </div>
          ) : (
            <>
              {cuerpo}
              {leyenda ? (
                <div className="whitespace-pre-wrap break-words px-1.5 pt-1 text-[14px] leading-[19px] text-[#111b21]">
                  {leyenda}
                  <span className={`inline-block ${entrante ? "w-10" : "w-14"}`} aria-hidden="true" />
                </div>
              ) : (
                <div className="h-4" />
              )}
            </>
          )}
          <span
            className={`absolute bottom-1 right-2 flex items-center gap-0.5 text-[11px] leading-none ${
              sinFondo ? "rounded bg-black/40 px-1 py-0.5 text-white" : "text-slate-500"
            }`}
          >
            {m.editado ? (
            <span className="mr-1 italic" data-testid={`wa-editado-${m.id}`}>
              Editado
            </span>
          ) : null}
          {horaCorta(m.created_at)}
            {!entrante && real && !m.simulado && <Tildes m={m} />}
          </span>
          {reacciones.length > 0 && (
            <div
              className={`absolute -bottom-3.5 ${entrante ? "left-2" : "right-2"} rounded-full border border-slate-100 bg-white px-1.5 py-0.5 text-sm leading-none shadow-sm`}
              data-testid={`wa-reaccion-${m.id}`}
            >
              {reacciones.map((r) => r.emoji).join("")}
            </div>
          )}
        </div>
        {!entrante && real && m.simulado && (
          <div className="mt-0.5 px-1 text-[10px] text-slate-500">no salió: el canal está en simulación</div>
        )}
        {fallido && (
          <div className="mt-0.5 flex items-start gap-1 px-1 text-[11px] text-red-600" title={m.error || undefined} data-testid={`wa-fallido-${m.id}`}>
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
            <span>No se envió{motivoFallo ? `: ${motivoFallo}` : ""}</span>
          {ctx && ctx.puedeReintentar ? (
            <button
              type="button"
              onClick={() => ctx.onReintentar(m)}
              disabled={ctx.ocupado === m.id}
              className="ml-1 inline-flex items-center gap-1 rounded border border-red-200 px-1.5 py-0.5 text-[11px] font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
              data-testid={`wa-reintentar-${m.id}`}
            >
              <RotateCcw className="h-3 w-3" />
              Reintentar
            </button>
          ) : null}
          </div>
        )}
      </div>
      {/* PROMPT 98: enseñarle la respuesta a lo que preguntó el cliente, ahí mismo. */}
      {entrante && onEnsenar && (
        <button
          type="button"
          onClick={() => onEnsenar(m)}
          title="Enseñarle la respuesta a esto"
          className="mb-1 shrink-0 text-slate-400/70 hover:text-slate-700"
          data-testid={`wa-ensenar-${m.id}`}
        >
          <GraduationCap className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// Separadores de dia, nombre de quien escribio cuando cambia y las reacciones pegadas al
// mensaje al que reaccionan (como en WhatsApp).
function armarFilas(mensajes) {
  const conWamid = new Set(mensajes.map((m) => m.wa_message_id).filter(Boolean));
  const reacciones = {};
  const visibles = [];
  for (const m of mensajes) {
    const destino = m.tipo === "reaccion" ? m.reaccion?.a : null;
    if (destino && conWamid.has(destino)) {
      const lista = (reacciones[destino] || []).filter((r) => r.direccion !== m.direccion);
      if (m.reaccion?.emoji) lista.push({ emoji: m.reaccion.emoji, direccion: m.direccion });
      reacciones[destino] = lista;
      continue;
    }
    visibles.push(m);
  }
  const filas = [];
  let dia = null;
  let anterior = null;
  for (const m of visibles) {
    const d = aFecha(m.created_at);
    const clave = d ? d.toDateString() : "";
    if (clave !== dia) {
      filas.push({ tipo: "dia", id: `dia-${clave || filas.length}`, texto: d ? etiquetaDia(d) : "" });
      dia = clave;
      anterior = null;
    }
    if (m.direccion === "sistema") {
      // PROMPT 143: avisos de WhatsApp (el cliente cambió de número, etc.): una etiqueta, no una burbuja.
      filas.push({ tipo: "sistema", id: m.id, texto: m.texto || "" });
      continue;
    }
    if (m.direccion === "interna") {
      // PROMPT 149: nota del equipo (el cliente no la ve); no corta el agrupado de burbujas.
      filas.push({ tipo: "nota", id: m.id, m });
      continue;
    }
    const primero = !anterior || anterior.direccion !== m.direccion;
    filas.push({
      tipo: "msg",
      id: m.id,
      m,
      primero,
      mostrarRemitente: m.direccion !== "entrante" && (primero || quien(anterior) !== quien(m)),
      reacciones: (m.wa_message_id && reacciones[m.wa_message_id]) || [],
    });
    anterior = m;
  }
  return filas;
}

function Ampliada({ foto, onCerrar }) {
  useEffect(() => {
    if (!foto) return undefined;
    const tecla = (e) => {
      if (e.key === "Escape") onCerrar();
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [foto, onCerrar]);
  if (!foto) return null;
  const parar = (e) => e.stopPropagation();
  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-slate-950/90" onClick={onCerrar} role="dialog" aria-modal="true" data-testid="wa-foto-grande">
      <div className="flex items-center justify-end gap-1 p-3" onClick={parar}>
        <button
          type="button"
          onClick={() => bajarUrl(foto.url, foto.nombre)}
          className="rounded-full p-2 text-white/80 hover:bg-white/10 hover:text-white"
          title="Descargar"
        >
          <Download className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={onCerrar}
          className="rounded-full p-2 text-white/80 hover:bg-white/10 hover:text-white"
          title="Cerrar (Esc)"
          data-testid="wa-foto-cerrar"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center p-4">
        <img src={foto.url} alt={foto.leyenda || "Foto"} className="max-h-full max-w-full rounded object-contain" onClick={parar} />
      </div>
      {foto.leyenda && (
        <p className="px-4 pb-6 text-center text-sm text-white/90" onClick={parar}>{foto.leyenda}</p>
      )}
    </div>,
    document.body
  );
}

// ------------------------------------------------------------------ hilo
function Hilo({ conversacion, onEnsenar, vacio, onArchivos, onCargarAnteriores, onAbrirConversacion }) {
  const mensajes = useMemo(() => conversacion?.mensajes || [], [conversacion]);
  const filas = useMemo(() => armarFilas(mensajes), [mensajes]);
  const real = conversacion?.canal === "whatsapp";
  const caja = useRef(null);
  const abajo = useRef(true);
  const bajando = useRef(false);
  const arrastres = useRef(0);
  const [lejos, setLejos] = useState(false);
  const [hayNuevos, setHayNuevos] = useState(false);
  const [arrastrando, setArrastrando] = useState(false);
  const [foto, setFoto] = useState(null);
  const convId = conversacion?.id || null;
  const ultimo = mensajes.length ? mensajes[mensajes.length - 1] : null;
  const primerId = mensajes.length ? mensajes[0].id : null;
  const [cargandoAntes, setCargandoAntes] = useState(false);
  const preservar = useRef(null);
  // PROMPT 143: la conversación anterior del mismo cliente en este número (si hay otras).
  const previa = useMemo(() => {
    const creada = aFecha(conversacion?.created_at);
    if (creada === null) return null;
    const hallada = (conversacion?.anteriores || []).find((a) => {
      const f = aFecha(a.created_at);
      return f !== null && f.getTime() < creada.getTime();
    });
    return hallada || null;
  }, [conversacion]);

  const irAbajo = useCallback((suave) => {
    const el = caja.current;
    if (!el) return;
    if (suave) {
      bajando.current = true;
      setTimeout(() => {
        bajando.current = false;
      }, 1000);
    }
    if (typeof el.scrollTo === "function") el.scrollTo({ top: el.scrollHeight, behavior: suave ? "smooth" : "auto" });
    else el.scrollTop = el.scrollHeight;
    abajo.current = true;
    setLejos(false);
    setHayNuevos(false);
  }, []);

  // Otra conversacion: directo al ultimo mensaje.
  useLayoutEffect(() => {
    irAbajo(false);
  }, [convId, irAbajo]);
  // Llega un mensaje: si estaba abajo (o lo mando uno mismo) baja; si estaba leyendo mas
  // arriba, aparece la flecha para bajar.
  const ultimoId = ultimo?.id;
  const ultimoPropio = ultimo?.autor === "persona" && !ultimo?.desde_celular;
  useEffect(() => {
    if (!ultimoId) return;
    if (abajo.current || ultimoPropio) irAbajo(true);
    else setHayNuevos(true);
  }, [ultimoId, ultimoPropio, irAbajo]);

  // Como WhatsApp: leyendo mas arriba aparece la flecha para bajar (con un punto verde si
  // llego algo nuevo).
  const alMover = () => {
    const el = caja.current;
    if (!el) return;
    const cerca = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (cerca) bajando.current = false;
    abajo.current = cerca || bajando.current;
    setLejos(!abajo.current);
    if (cerca) setHayNuevos(false);
  };
  const alCargarFoto = useCallback(() => {
    if (abajo.current) irAbajo(false);
  }, [irAbajo]);
  const cerrarFoto = useCallback(() => setFoto(null), []);

  // PROMPT 143: los mensajes más viejos se agregan ARRIBA; lo que se está leyendo no se mueve.
  useLayoutEffect(() => {
    const el = caja.current;
    const guardado = preservar.current;
    if (!el || !guardado) return;
    preservar.current = null;
    if (guardado.id === convId) el.scrollTop = guardado.top + (el.scrollHeight - guardado.alto);
  }, [primerId, convId]);
  const cargarMasAntiguos = async () => {
    const el = caja.current;
    if (!onCargarAnteriores || cargandoAntes) return;
    if (el) preservar.current = { id: convId, alto: el.scrollHeight, top: el.scrollTop };
    setCargandoAntes(true);
    try {
      await onCargarAnteriores();
    } finally {
      setCargandoAntes(false);
    }
  };

  const conArchivos = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  return (
    <div
      className="relative flex min-h-0 flex-1 flex-col border-b border-slate-200"
      onDragEnter={(e) => {
        if (!conArchivos(e)) return;
        e.preventDefault();
        arrastres.current += 1;
        setArrastrando(true);
      }}
      onDragOver={(e) => {
        if (conArchivos(e)) e.preventDefault();
      }}
      onDragLeave={() => {
        arrastres.current = Math.max(0, arrastres.current - 1);
        if (!arrastres.current) setArrastrando(false);
      }}
      onDrop={(e) => {
        if (!conArchivos(e)) return;
        e.preventDefault();
        arrastres.current = 0;
        setArrastrando(false);
        onArchivos(Array.from(e.dataTransfer.files || []));
      }}
    >
      <div
        ref={caja}
        onScroll={alMover}
        className="min-h-0 flex-1 overflow-y-auto bg-[#efeae2] px-3 py-3 sm:px-6"
        data-testid="wa-hilo"
      >
      {(conversacion?.hay_anteriores || previa) && (
        <div className="mb-2 flex flex-wrap items-center justify-center gap-2" data-testid="wa-anteriores">
          {conversacion?.hay_anteriores && (
            <button
              type="button"
              onClick={cargarMasAntiguos}
              disabled={cargandoAntes}
              className="rounded-full bg-white/95 px-3 py-1 text-[11.5px] font-medium text-slate-700 shadow-sm hover:bg-white disabled:opacity-60"
              data-testid="wa-cargar-anteriores"
            >
              {cargandoAntes ? "Cargando…" : "Cargar mensajes anteriores"}
            </button>
          )}
          {previa && onAbrirConversacion && (
            <button
              type="button"
              onClick={() => onAbrirConversacion(previa)}
              className="rounded-full bg-white/95 px-3 py-1 text-[11.5px] font-medium text-slate-700 shadow-sm hover:bg-white"
              data-testid="wa-conversacion-anterior"
            >
              Ver la conversación anterior ({fechaCorta(aFecha(previa.created_at))})
            </button>
          )}
        </div>
      )}
      {filas.length
          ? filas.map((f) =>
              f.tipo === "dia" ? (
                <div key={f.id} className="sticky top-1 z-10 my-2 flex justify-center">
                  <span className="rounded-md bg-white/95 px-2.5 py-1 text-[11.5px] text-slate-600 shadow-sm">{f.texto}</span>
                </div>
              ) : f.tipo === "nota" ? (
              <NotaEquipo key={f.id} nota={f.m} />
            ) : f.tipo === "sistema" ? (
              <div key={f.id} className="my-2 flex justify-center" data-testid="wa-sistema">
                <span className="max-w-[85%] rounded-md bg-slate-200/90 px-2.5 py-1 text-center text-[11.5px] text-slate-600">
                  {f.texto}
                </span>
              </div>
              ) : (
              <Burbuja
              key={f.id}
                  m={f.m}
                  real={real}
                  primero={f.primero}
                  mostrarRemitente={f.mostrarRemitente}
                  reacciones={f.reacciones}
                  onEnsenar={onEnsenar}
                  onCargado={alCargarFoto}
                  onAmpliar={setFoto}
                />
              )
            )
          : vacio}
      </div>
      {(lejos || hayNuevos) && (
        <button
          type="button"
          onClick={() => irAbajo(true)}
          className="absolute bottom-4 right-5 flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-600 shadow-md hover:text-slate-900"
          title={hayNuevos ? "Hay mensajes nuevos" : "Ir a lo último"}
          data-testid="wa-ir-abajo"
        >
          <ArrowDown className="h-5 w-5" />
          {hayNuevos && (
            <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white" data-testid="wa-hay-nuevos" />
          )}
        </button>
      )}
      {arrastrando && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center border-2 border-dashed border-emerald-500 bg-emerald-50/85 text-sm font-medium text-emerald-700">
          Soltá los archivos para mandárselos al cliente
        </div>
      )}
      <Ampliada foto={foto} onCerrar={cerrarFoto} />
    </div>
  );
}

// ------------------------------------------------------------------ mandar archivos
const MAX_SUBIDA = 16 * 1024 * 1024;
const MAX_ADJUNTOS = 10;
const ACEPTA =
  "image/*,audio/*,video/mp4,video/3gpp,.mp4,.3gp,application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt";

function clasificar(f) {
  const t = (f.type || "").toLowerCase();
  const n = f.name || "";
  if (!f.size) return { error: "está vacío." };
  if (f.size > MAX_SUBIDA) return { error: "pesa más de 16 MB: WhatsApp no deja mandarlo." };
  if (/heic|heif/.test(t) || /\.(heic|heif)$/i.test(n)) return { error: "las fotos HEIC del iPhone no se pueden mandar: pasala a JPG." };
  if (t === "image/svg+xml") return { error: "los SVG no se pueden mandar por WhatsApp." };
  if (t.startsWith("image/")) return { tipo: "imagen" };
  // PROMPT 148: MP4 y 3GP de hasta 16 MB se mandan como video (el servidor revisa que se pueda reproducir).
  if (t === "video/mp4" || t === "video/3gpp" || /\.(mp4|3gp|3gpp)$/i.test(n)) return { tipo: "video" };
  if (t.startsWith("video/") && !/\.webm$/i.test(n)) return { error: "es un video que WhatsApp no acepta: tiene que ser MP4 o 3GP." };
  if (t.startsWith("audio/") || /\.(mp3|m4a|aac|amr|ogg|opus|oga|webm)$/i.test(n)) return { tipo: "audio" };
  if (t === "application/pdf" || /\.(pdf|docx?|xlsx?|pptx?|txt)$/i.test(n)) return { tipo: "documento" };
  return { error: "no se puede mandar por WhatsApp (sí fotos, audios, PDF, Word, Excel y PowerPoint)." };
}

export async function subirArchivo(conversacionId, archivo, { texto, duracion, notaDeVoz, nombre, onProgreso, responderA } = {}) {
  const fd = new FormData();
  fd.append("archivo", archivo, nombre || archivo.name || "archivo");
  if (texto && texto.trim()) fd.append("texto", texto.trim());
  if (duracion) fd.append("duracion", String(Math.round(duracion * 10) / 10));
  if (notaDeVoz) fd.append("nota_de_voz", "true");
  if (responderA) fd.append("responder_a", responderA);
  const { data } = await api.post(`/whatsapp/conversaciones/${conversacionId}/media`, fd, {
    onUploadProgress: (e) => onProgreso?.(e.total ? Math.round((e.loaded / e.total) * 100) : null),
  });
  return data;
}

function VistaAdjuntos({ adjuntos, setAdjuntos, conversacionId, onEnviado }) {
  const ctxCita = useContextoChat();
  const [enviando, setEnviando] = useState(false);
  const [progreso, setProgreso] = useState(null);
  const quitar = (id) => {
    const item = adjuntos.find((a) => a.id === id);
    if (item?.url) URL.revokeObjectURL(item.url);
    setAdjuntos((lista) => lista.filter((a) => a.id !== id));
  };
  const cancelar = () => {
    adjuntos.forEach((a) => a.url && URL.revokeObjectURL(a.url));
    setAdjuntos([]);
  };
  const enviarTodo = async () => {
    if (enviando) return;
    setEnviando(true);
    // PROMPT 148: si se está respondiendo a un mensaje, la cita va con el primer archivo.
    let citaId = (ctxCita && ctxCita.cita && ctxCita.cita.id) || null;
    for (const item of adjuntos) {
      try {
        setProgreso({ id: item.id, pct: 0 });
        const data = await subirArchivo(conversacionId, item.file, {
          texto: item.leyenda,
          responderA: citaId,
          onProgreso: (pct) => setProgreso({ id: item.id, pct }),
        });
        if (item.url) URL.revokeObjectURL(item.url);
        setAdjuntos((lista) => lista.filter((a) => a.id !== item.id));
        onEnviado?.(data);
        if (citaId) {
          citaId = null;
          ctxCita.usarCita();
        }
      } catch (e) {
        toast.error(`${item.file.name || "Archivo"}: ${await mensajeDeError(e)}`);
        break;
      }
    }
    setProgreso(null);
    setEnviando(false);
  };
  const cambiarLeyenda = (id, leyenda) =>
    setAdjuntos((lista) => lista.map((a) => (a.id === id ? { ...a, leyenda } : a)));

  return (
    <div className="space-y-2 bg-[#f0f2f5] p-3" data-testid="wa-adjuntos">
      <div className="max-h-64 space-y-2 overflow-y-auto">
        {adjuntos.map((a, i) => (
          <div key={a.id} className="flex items-center gap-3 rounded-lg bg-white p-2 shadow-sm">
            {a.url ? (
              <img src={a.url} alt="" className="h-14 w-14 shrink-0 rounded object-cover" />
            ) : (
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded bg-slate-100 text-slate-500">
                {a.tipo === "audio" ? (
                <Headphones className="h-6 w-6" />
              ) : a.tipo === "video" ? (
                <Video className="h-6 w-6" />
              ) : (
                <FileText className="h-6 w-6" />
              )}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-slate-800">{a.file.name || "archivo"}</div>
              <div className="text-[11px] text-slate-500">
                {tamano(a.file.size)}
                {progreso?.id === a.id ? ` · enviando${progreso.pct != null ? ` ${progreso.pct}%` : "…"}` : ""}
              </div>
              {a.tipo !== "audio" && (
                <input
                  value={a.leyenda}
                  onChange={(e) => cambiarLeyenda(a.id, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      enviarTodo();
                    }
                  }}
                  maxLength={1024}
                  placeholder="Agregá un comentario…"
                  disabled={enviando}
                  autoFocus={i === 0}
                  className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  data-testid={`wa-adjunto-leyenda-${i}`}
                />
              )}
            </div>
            <button
              type="button"
              onClick={() => quitar(a.id)}
              disabled={enviando}
              className="shrink-0 rounded-full p-1.5 text-slate-400 hover:bg-black/5 hover:text-slate-700 disabled:opacity-40"
              title="Quitar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={cancelar}
          disabled={enviando}
          className="rounded-md px-3 py-2 text-sm text-slate-600 hover:bg-black/5 disabled:opacity-40"
          data-testid="wa-adjuntos-cancelar"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={enviarTodo}
          disabled={enviando || !adjuntos.length}
          className="flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          data-testid="wa-adjuntos-enviar"
        >
          {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
          Enviar{adjuntos.length > 1 ? ` ${adjuntos.length}` : ""}
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ notas de voz
// WhatsApp solo muestra como nota de voz un audio OGG/OPUS en mono. Firefox graba en OGG;
// Chrome y Edge en WebM, y el backend lo pasa a OGG. Se graba en mono (el microfono pasa por
// un mezclador de un canal) y a 20 kbps, como las notas de voz de WhatsApp: asi una nota de
// unos 3 minutos queda por debajo de 512 KB, que es hasta donde Meta muestra el boton de play.
const TIPOS_GRABACION = ["audio/ogg;codecs=opus", "audio/webm;codecs=opus", "audio/webm"];
const BITS_VOZ = 20000;
const MAX_GRABACION_S = 15 * 60;

function tipoGrabacion() {
  const MR = window.MediaRecorder;
  if (!MR || typeof MR.isTypeSupported !== "function") return "";
  return TIPOS_GRABACION.find((t) => MR.isTypeSupported(t)) || "";
}

function extensionGrabacion(tipo) {
  const t = String(tipo || "");
  if (t.includes("ogg")) return ".ogg";
  if (t.includes("mp4") || t.includes("aac")) return ".m4a";
  return ".webm";
}

function useGrabadora() {
  const [estado, setEstado] = useState("inactiva"); // inactiva | pidiendo | grabando | enviando | revisar
  const [segundos, setSegundos] = useState(0);
  const [grabacion, setGrabacion] = useState(null);
  const estadoRef = useRef("inactiva");
  const grabacionRef = useRef(null);
  const r = useRef({});

  const poner = useCallback((e) => {
    estadoRef.current = e;
    setEstado(e);
  }, []);

  const soltar = useCallback(() => {
    const x = r.current;
    clearInterval(x.reloj);
    if (x.stream) x.stream.getTracks().forEach((t) => t.stop());
    if (x.ctx && x.ctx.state !== "closed") x.ctx.close().catch(() => {});
    r.current = {};
  }, []);

  const revisar = useCallback(
    (g) => {
      if (grabacionRef.current?.url && grabacionRef.current !== g) URL.revokeObjectURL(grabacionRef.current.url);
      const conUrl = g ? { ...g, url: g.url || URL.createObjectURL(g.blob) } : null;
      grabacionRef.current = conUrl;
      setGrabacion(conUrl);
      poner(conUrl ? "revisar" : "inactiva");
    },
    [poner]
  );

  const detener = useCallback(
    () =>
      new Promise((listo) => {
        const x = r.current;
        if (!x.rec || x.rec.state === "inactive") {
          soltar();
          listo(null);
          return;
        }
        x.rec.onstop = () => {
          const tipo = x.rec.mimeType || x.partes[0]?.type || "audio/webm";
          const blob = new Blob(x.partes, { type: tipo });
          const duracion = (Date.now() - x.inicio) / 1000;
          soltar();
          listo(blob.size ? { blob, tipo, duracion } : null);
        };
        try {
          x.rec.stop();
        } catch {
          soltar();
          listo(null);
        }
      }),
    [soltar]
  );

  const descartar = useCallback(() => {
    const x = r.current;
    if (x.rec && x.rec.state !== "inactive") {
      x.rec.ondataavailable = null;
      x.rec.onstop = null;
      try {
        x.rec.stop();
      } catch {
        /* ya estaba parada */
      }
    }
    soltar();
    revisar(null);
    setSegundos(0);
  }, [soltar, revisar]);

  const empezar = useCallback(async () => {
    if (estadoRef.current !== "inactiva") return;
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      toast.error("Para grabar, la plataforma tiene que estar abierta con https.");
      return;
    }
    if (typeof window.MediaRecorder === "undefined") {
      toast.error("Este navegador no puede grabar audio. Probá con Chrome o Edge.");
      return;
    }
    poner("pidiendo");
    setSegundos(0);
    // El mezclador se crea ya (con el clic): si se crea despues, el navegador lo puede frenar.
    const AC = window.AudioContext || window.webkitAudioContext;
    let ctx = null;
    try {
      ctx = AC ? new AC() : null;
    } catch {
      ctx = null;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (e) {
      if (ctx) ctx.close().catch(() => {});
      poner("inactiva");
      toast.error(
        e?.name === "NotAllowedError" || e?.name === "SecurityError"
          ? "El navegador no dejó usar el micrófono. Habilitalo en el candado que está a la izquierda de la dirección."
          : e?.name === "NotFoundError"
            ? "No se encontró ningún micrófono conectado."
            : "No se pudo usar el micrófono."
      );
      return;
    }
    if (estadoRef.current !== "pidiendo") {
      // lo cancelaron mientras el navegador pedia permiso
      stream.getTracks().forEach((t) => t.stop());
      if (ctx) ctx.close().catch(() => {});
      return;
    }
    let pista = stream;
    if (ctx) {
      try {
        if (ctx.state === "suspended") {
          await Promise.race([ctx.resume(), new Promise((ok) => setTimeout(ok, 800))]);
        }
        if (ctx.state !== "running") throw new Error("mezclador frenado");
        const fuente = ctx.createMediaStreamSource(stream);
        const destino = ctx.createMediaStreamDestination();
        destino.channelCount = 1;
        destino.channelCountMode = "explicit";
        destino.channelInterpretation = "speakers";
        fuente.connect(destino);
        pista = destino.stream;
      } catch {
        ctx.close().catch(() => {});
        ctx = null;
        pista = stream;
      }
    }
    const tipo = tipoGrabacion();
    let rec;
    try {
      rec = new window.MediaRecorder(pista, tipo ? { mimeType: tipo, audioBitsPerSecond: BITS_VOZ } : { audioBitsPerSecond: BITS_VOZ });
    } catch {
      rec = new window.MediaRecorder(stream);
    }
    const partes = [];
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size) partes.push(e.data);
    };
    r.current = { rec, stream, ctx, partes, inicio: Date.now() };
    rec.start(250);
    poner("grabando");
    r.current.reloj = setInterval(async () => {
      const s = (Date.now() - (r.current.inicio || Date.now())) / 1000;
      setSegundos(s);
      if (s >= MAX_GRABACION_S && estadoRef.current === "grabando") {
        poner("enviando");
        const g = await detener();
        revisar(g);
        toast.message("La nota de voz llegó a 15 minutos y se cortó. Escuchala y mandala, o descartala.");
      }
    }, 250);
  }, [poner, detener, revisar]);

  // Termina la grabacion y la devuelve para mandarla.
  const terminar = useCallback(async () => {
    if (estadoRef.current === "revisar") {
      poner("enviando");
      return grabacionRef.current;
    }
    if (estadoRef.current !== "grabando") return null;
    poner("enviando");
    return detener();
  }, [poner, detener]);

  // Si se cierra la pantalla grabando, se apaga el microfono.
  const apagar = useCallback(() => {
    const x = r.current;
    if (x.rec && x.rec.state !== "inactive") {
      x.rec.ondataavailable = null;
      x.rec.onstop = null;
      try {
        x.rec.stop();
      } catch {
        /* ya estaba parada */
      }
    }
    soltar();
    if (grabacionRef.current?.url) URL.revokeObjectURL(grabacionRef.current.url);
  }, [soltar]);
  useEffect(() => apagar, [apagar]);

  return { estado, segundos, grabacion, empezar, terminar, descartar, revisar };
}

function BarraGrabacion({ grab, conversacionId, onEnviado }) {
  const ctxCita = useContextoChat();
  const enviar = async () => {
    const g = await grab.terminar();
    if (!g) {
      grab.descartar();
      toast.error("La grabación quedó vacía. Probá de nuevo.");
      return;
    }
    try {
      const data = await subirArchivo(conversacionId, g.blob, {
        duracion: g.duracion,
        notaDeVoz: true,
        nombre: `nota_de_voz${extensionGrabacion(g.tipo)}`,
        responderA: ctxCita && ctxCita.cita ? ctxCita.cita.id : undefined,
      });
      grab.descartar();
      onEnviado?.(data);
      if (ctxCita && ctxCita.cita) ctxCita.usarCita();
    } catch (e) {
      toast.error(`No se pudo mandar la nota de voz: ${await mensajeDeError(e)}`);
      grab.revisar(g);
    }
  };
  const enviando = grab.estado === "enviando";
  return (
    <div className="flex items-center gap-3 bg-[#f0f2f5] px-3 py-2.5" data-testid="wa-grabando">
      <button
        type="button"
        onClick={grab.descartar}
        disabled={enviando}
        className="rounded-full p-2 text-slate-500 hover:bg-black/5 hover:text-red-600 disabled:opacity-40"
        title="Descartar"
        data-testid="wa-grabacion-descartar"
      >
        <Trash2 className="h-5 w-5" />
      </button>
      {grab.estado === "revisar" && grab.grabacion ? (
        <audio src={grab.grabacion.url} controls className="h-10 min-w-0 flex-1" />
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-2 text-sm text-slate-700">
          <span className={`h-2.5 w-2.5 shrink-0 rounded-full bg-red-500 ${grab.estado === "grabando" ? "animate-pulse" : ""}`} />
          <span className="tabular-nums" data-testid="wa-grabacion-tiempo">{mmss(grab.segundos)}</span>
          <span className="truncate text-slate-500">
            {grab.estado === "pidiendo" ? "Pidiendo el micrófono…" : enviando ? "Enviando…" : "Grabando nota de voz…"}
          </span>
        </div>
      )}
      <button
        type="button"
        onClick={enviar}
        disabled={enviando || grab.estado === "pidiendo"}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
        title="Enviar nota de voz"
        data-testid="wa-grabacion-enviar"
      >
        {enviando ? <Loader2 className="h-5 w-5 animate-spin" /> : <SendHorizontal className="h-5 w-5" />}
      </button>
    </div>
  );
}

// ------------------------------------------------------------------ escribir
function Compositor({
  conversacionId, puedeAdjuntar, simulandoCliente, texto, setTexto, enviando, onEnviarTexto,
  placeholder, deshabilitado, etiquetaEnviar, onArchivos, onEnviado, onGrabando, onPlantilla,
  marcaSlug, valoresSugeridos, onNota, // PROMPT 149
}) {
  const area = useRef(null);
  const elegir = useRef(null);
  // PROMPT 149: respuestas rapidas ("/" al empezar el texto y el boton del rayo).
  const rr = useRespuestasRapidas({
    marcaSlug,
    texto,
    setTexto,
    valores: valoresSugeridos,
    area,
    activo: !simulandoCliente && !deshabilitado,
  });
  const grab = useGrabadora();
  const grabando = grab.estado !== "inactiva";
  useEffect(() => {
    onGrabando(grabando);
  }, [grabando, onGrabando]);
  useEffect(() => () => onGrabando(false), [onGrabando]);
  useLayoutEffect(() => {
    const ta = area.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 128)}px`;
  }, [texto]);

  if (grabando) {
    return <BarraGrabacion grab={grab} conversacionId={conversacionId} onEnviado={onEnviado} />;
  }
  const hayTexto = !!texto.trim();
  const mostrarMicrofono = puedeAdjuntar && !hayTexto;
  // PROMPT 148: el emoji va donde está el cursor; WhatsApp no deja más de 4096 caracteres.
  const pasadoDeLargo = texto.length > MAX_TEXTO_WA;
  const insertarEmoji = (emoji) => {
    const ta = area.current;
    const ini = ta && typeof ta.selectionStart === "number" ? ta.selectionStart : texto.length;
    const fin = ta && typeof ta.selectionEnd === "number" ? ta.selectionEnd : ini;
    setTexto(`${texto.slice(0, ini)}${emoji}${texto.slice(fin)}`);
    const pos = ini + emoji.length;
    requestAnimationFrame(() => {
      const campo = area.current;
      if (!campo) return;
      campo.focus();
      if (campo.setSelectionRange) campo.setSelectionRange(pos, pos);
    });
  };
  return (
    <div className="relative flex items-end gap-2 bg-[#f0f2f5] px-3 py-2.5">
      {rr.menu}
      {texto.length > 3500 ? (
        <span
          className={`absolute -top-5 right-4 rounded bg-white/90 px-1.5 text-[11px] ${
            pasadoDeLargo ? "font-medium text-red-600" : "text-slate-500"
          }`}
          data-testid="wa-contador"
        >
          {texto.length}/{MAX_TEXTO_WA}
        </span>
      ) : null}
      {simulandoCliente ? (
        <span className="mb-2.5 shrink-0" title="Escribís como si fueras el cliente (prueba)">
          <User className="h-5 w-5 text-slate-400" />
        </span>
      ) : (
        <button
          type="button"
          onClick={() => elegir.current?.click()}
          disabled={!puedeAdjuntar}
          className="mb-0.5 shrink-0 rounded-full p-2 text-slate-500 hover:bg-black/5 hover:text-slate-700 disabled:opacity-40"
          title="Adjuntar foto, audio o documento"
          data-testid="wa-adjuntar"
        >
          <Paperclip className="h-5 w-5" />
        </button>
      )}
      {onPlantilla && !simulandoCliente ? (
        <button
          type="button"
          onClick={onPlantilla}
          disabled={deshabilitado}
          className="mb-0.5 inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-[13px] font-medium text-slate-600 hover:bg-black/5 hover:text-slate-800 disabled:opacity-40"
          title="Mandar una plantilla aprobada"
          data-testid="wa-plantilla"
        >
          <FileText className="h-5 w-5" />
          <span className="hidden sm:inline">Plantilla</span>
        </button>
      ) : null}
      {!simulandoCliente ? (
          <BotonRespuestasRapidas onClick={rr.alternar} deshabilitado={deshabilitado} activo={rr.abierto} />
        ) : null}
        {onNota && !simulandoCliente ? <BotonNota onClick={onNota} className="mb-0.5" /> : null}
        <BotonEmojis onElegir={insertarEmoji} deshabilitado={deshabilitado} />
      <input
        ref={elegir}
        type="file"
        multiple
        accept={ACEPTA}
        className="hidden"
        onChange={(e) => {
          onArchivos(Array.from(e.target.files || []));
          e.target.value = "";
        }}
        data-testid="wa-archivo-input"
      />
      <textarea
        ref={area}
        rows={1}
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        onKeyDown={(e) => {
          if (rr.alTeclear(e)) return; // PROMPT 149
          if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            if (!enviando && !pasadoDeLargo) onEnviarTexto();
          }
        }}
        onPaste={(e) => {
          const archivos = Array.from(e.clipboardData?.files || []);
          if (archivos.length && puedeAdjuntar) {
            e.preventDefault();
            onArchivos(archivos);
          }
        }}
        placeholder={placeholder}
        disabled={deshabilitado}
        className="min-h-[40px] flex-1 resize-none rounded-lg border border-transparent bg-white px-3 py-2.5 text-base leading-5 text-slate-900 shadow-sm lg:text-sm placeholder:text-slate-400 focus:border-emerald-300 focus:outline-none disabled:bg-slate-100 disabled:text-slate-400"
        data-testid="wa-texto"
      />
      {mostrarMicrofono ? (
        <button
          type="button"
          onClick={grab.empezar}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
          title="Grabar nota de voz"
          data-testid="wa-grabar"
        >
          <Mic className="h-5 w-5" />
        </button>
      ) : (
        <button
          type="button"
          onClick={onEnviarTexto}
          disabled={enviando || !hayTexto || deshabilitado || pasadoDeLargo}
          className="flex h-10 shrink-0 items-center justify-center gap-2 rounded-full bg-emerald-600 px-3 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50 sm:px-4"
          title={etiquetaEnviar}
          data-testid="wa-enviar"
        >
          {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
          <span className="hidden sm:inline">{etiquetaEnviar}</span>
        </button>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ todo junto
// `barra`: lo que va entre el hilo y el cuadro de escribir (los modos y la ventana de 24 h).
export default function ChatWhatsApp({
  conversacion, onEnsenar, vacio, barra, modoPersona, ventanaCerrada, texto, setTexto, enviando,
  onEnviarTexto, placeholder, etiquetaEnviar, onArchivoEnviado, onCargarAnteriores, onAbrirConversacion,
    // PROMPT 145: en una conversacion archivada, "Desarchivar" reemplaza al cuadro de escribir.
    onDesarchivar,
    bloqueo,
    // PROMPT 147: abre el selector de plantillas (solo en las conversaciones reales de WhatsApp)
  onPlantilla,
  // PROMPT 148: { cita, setCita, conversaciones, marcas } (la cita en curso y la lista para reenviar).
  acciones,
}) {
  const convId = conversacion?.id || null;
  const cerrada = conversacion?.estado === "cerrada";
  const bloqueado = Boolean(conversacion?.bloqueado); // PROMPT 150
  // PROMPT 148: citar, reaccionar, reenviar, ver información y reintentar.
  const { contexto: contextoChat, dialogos: dialogosChat } = useAccionesChat({
    conversacion,
    acciones,
    modoPersona,
    ventanaCerrada,
    cerrada,
    onHecho: onArchivoEnviado,
  });
  const puedeAdjuntar = !!convId && modoPersona && !ventanaCerrada && !cerrada;
  const sinVentana = !!onPlantilla && !!convId && modoPersona && ventanaCerrada && !cerrada;
  const [adjuntos, setAdjuntos] = useState([]);
  // PROMPT 149: nota interna (la zona de escribir pasa a amarillo y el cliente no la ve).
  const [modoNota, setModoNota] = useState(false);
  useEffect(() => {
    setModoNota(false);
  }, [convId]);
  const notaDisponible = !!convId && conversacion?.canal === "whatsapp";
  const adjuntosRef = useRef(adjuntos);
  adjuntosRef.current = adjuntos;
  const grabandoRef = useRef(false);
  const alGrabar = useCallback((v) => {
    grabandoRef.current = v;
  }, []);

  // Lo que estaba por mandarse en otra conversacion no pasa a esta.
  useEffect(() => {
    adjuntosRef.current.forEach((a) => a.url && URL.revokeObjectURL(a.url));
    setAdjuntos([]);
  }, [convId]);

  const agregar = useCallback(
    (archivos) => {
      const lista = Array.from(archivos || []).filter(Boolean);
      if (!lista.length) return;
      if (!convId) return toast.message("Abrí una conversación para mandar archivos.");
      if (!modoPersona) return toast.message("Para mandar archivos, elegí “Responderle al cliente”.");
      if (cerrada) return toast.message("La conversación está cerrada: reabrila para escribir.");
      if (bloqueado) return toast.message("El cliente está bloqueado: desbloquealo para escribirle.");
      if (ventanaCerrada) {
        return toast.error("Pasaron 24 horas desde el último mensaje del cliente: WhatsApp no deja mandar archivos.");
      }
      if (grabandoRef.current) return toast.message("Terminá o descartá la nota de voz antes de adjuntar archivos.");
      const lugar = MAX_ADJUNTOS - adjuntosRef.current.length;
      const nuevos = [];
      for (const f of lista) {
        const c = clasificar(f);
        if (c.error) {
          toast.error(`${f.name || "Archivo"}: ${c.error}`);
          continue;
        }
        if (nuevos.length >= lugar) {
          toast.message(`Se mandan hasta ${MAX_ADJUNTOS} archivos por vez.`);
          break;
        }
        nuevos.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file: f,
          tipo: c.tipo,
          url: c.tipo === "imagen" ? URL.createObjectURL(f) : null,
          leyenda: "",
        });
      }
      if (nuevos.length) setAdjuntos((a) => [...a, ...nuevos]);
      return undefined;
    },
    [convId, modoPersona, cerrada, bloqueado, ventanaCerrada]
  );

  return (
    <ContextoChat.Provider value={contextoChat}>
    <>
      <Hilo
        conversacion={conversacion}
        onEnsenar={onEnsenar}
        vacio={vacio}
        onArchivos={agregar}
        onCargarAnteriores={onCargarAnteriores}
        onAbrirConversacion={onAbrirConversacion}
      />
      {barra}
      {/* PROMPT 144: el cuadro de escribir pegado abajo, por encima del borde del iPhone. */}
      <div className="shrink-0 bg-[#f0f2f5] pb-[env(safe-area-inset-bottom)]">
        <BarraRespuesta />
        {modoNota && notaDisponible ? (
          <NotaDelEquipo
            conversacionId={convId}
            onGuardada={() => {
              setModoNota(false);
              if (onArchivoEnviado) onArchivoEnviado({ conversacion: { id: convId } });
            }}
            onCancelar={() => setModoNota(false)}
          />
        ) : bloqueado && bloqueo ? (
          <ZonaBloqueado bloqueo={bloqueo} onNota={notaDisponible ? () => setModoNota(true) : undefined} />
        ) : cerrada && onDesarchivar ? (
          <div
            className="flex flex-wrap items-center justify-between gap-2 px-3 py-3"
            data-testid="wa-archivada"
          >
            <span className="min-w-0 text-sm text-slate-600">
              Conversación archivada: desarchivala para responder.
            </span>
            {notaDisponible ? <BotonNota onClick={() => setModoNota(true)} className="ml-auto" /> : null}
            <button
              type="button"
              onClick={onDesarchivar}
              data-testid="wa-desarchivar"
              className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-emerald-600 px-3 text-sm font-medium text-white hover:bg-emerald-700"
            >
              <ArchiveRestore className="h-4 w-4" /> Desarchivar
            </button>
          </div>
        ) : sinVentana ? (
          <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-3" data-testid="wa-ventana-cerrada">
            <span className="flex min-w-0 items-start gap-1.5 text-sm text-slate-600">
              <Clock className="mt-0.5 h-4 w-4 shrink-0" />
              Pasaron 24 horas desde el último mensaje del cliente. Para escribirle, mandá una plantilla aprobada.
            </span>
            {notaDisponible ? <BotonNota onClick={() => setModoNota(true)} className="ml-auto" /> : null}
            <button
              type="button"
              onClick={onPlantilla}
              data-testid="wa-elegir-plantilla"
              className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-emerald-600 px-3 text-sm font-medium text-white hover:bg-emerald-700"
            >
              <FileText className="h-4 w-4" /> Elegir plantilla
            </button>
          </div>
        ) : adjuntos.length ? (
          <VistaAdjuntos adjuntos={adjuntos} setAdjuntos={setAdjuntos} conversacionId={convId} onEnviado={onArchivoEnviado} />
        ) : (
          <Compositor
            key={convId || "nueva"}
            conversacionId={convId}
            puedeAdjuntar={puedeAdjuntar}
            simulandoCliente={!modoPersona || !convId}
            texto={texto}
            setTexto={setTexto}
            enviando={enviando}
            onEnviarTexto={onEnviarTexto}
            placeholder={placeholder}
            deshabilitado={modoPersona && ventanaCerrada}
            onPlantilla={modoPersona && !cerrada ? onPlantilla : undefined}
            etiquetaEnviar={etiquetaEnviar}
            onArchivos={agregar}
            onEnviado={onArchivoEnviado}
            onGrabando={alGrabar}
          marcaSlug={conversacion?.marca_slug || ""}
          valoresSugeridos={conversacion?.valores_sugeridos}
          onNota={notaDisponible ? () => setModoNota(true) : undefined}
          />
        )}
      </div>
      {dialogosChat}
    </>
    </ContextoChat.Provider>
  );
}
